import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { EyeIcon, EyeSlashIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { ERROR_CODES, logError, mapHttpStatusToError, getApiBaseUrl } from '../utils/errorCodes';

/**
 * ResetPasswordPage component for the Agent Nexus
 *
 * This component provides a form for users to reset their password using
 * a token received via email. The token is passed as a URL query parameter.
 *
 * Supports unified authentication across AgentNexus and NexusChat:
 * - When REACT_APP_POST_RESET_REDIRECT_URL is set, redirects to that URL after success
 * - URL parameter 'redirect' can override the default redirect destination
 * - This allows NexusChat users to be sent back to NexusChat login after reset
 */
const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  // Support redirect URL from query param or environment variable
  // Priority: URL param > env var > default '/login'
  const redirectParam = searchParams.get('redirect');
  const postResetRedirectUrl = redirectParam || process.env.REACT_APP_POST_RESET_REDIRECT_URL || null;

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(true);

  // Password strength indicators
  const [passwordStrength, setPasswordStrength] = useState({
    hasMinLength: false,
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false,
    hasSpecial: false,
  });

  // Check if token exists
  useEffect(() => {
    if (!token) {
      setTokenValid(false);
    }
  }, [token]);

  // Validate password strength as user types
  useEffect(() => {
    setPasswordStrength({
      hasMinLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecial: /[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/.test(password),
    });
  }, [password]);

  const isPasswordValid = Object.values(passwordStrength).every(Boolean);
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

  /**
   * Handle form submission
   * Sends new password to the backend with the reset token
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate passwords match
    if (password !== confirmPassword) {
      const errDef = ERROR_CODES.RESET_PASSWORD_MISMATCH;
      logError(errDef, { context: 'password_reset_form' });
      setError(`${errDef.message} (${errDef.code})`);
      return;
    }

    // Validate password strength
    if (!isPasswordValid) {
      const errDef = ERROR_CODES.RESET_PASSWORD_WEAK;
      logError(errDef, { context: 'password_reset_form' });
      setError(`${errDef.message} (${errDef.code})`);
      return;
    }

    setLoading(true);

    try {
      // Use getApiBaseUrl for auth endpoints (removes /api suffix)
      const apiUrl = getApiBaseUrl(true);
      const response = await fetch(`${apiUrl}/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, password }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        logError({ code: 'NEXUS-0000', message: 'Password reset successful', logLevel: 'info' }, {
          context: 'password_reset_success'
        });
        setSuccess(true);
      } else {
        // Map the HTTP status and server response to appropriate error
        const errDef = mapHttpStatusToError(response.status, 'password_reset');
        const serverMessage = data.detail || data.message;
        logError(errDef, {
          context: 'password_reset_api_error',
          status: response.status,
          serverMessage
        });
        // Show server message if available, otherwise use standard error message
        setError(serverMessage ? `${serverMessage} (${errDef.code})` : `${errDef.message} (${errDef.code})`);
      }
    } catch (err) {
      const errDef = err.message?.includes('fetch')
        ? ERROR_CODES.API_NETWORK_ERROR
        : ERROR_CODES.SYSTEM_UNEXPECTED;
      logError(errDef, {
        context: 'password_reset_exception',
        error: err.message
      });
      setError(`${errDef.message} (${errDef.code})`);
    } finally {
      setLoading(false);
    }
  };

  // Invalid or missing token state
  if (!tokenValid) {
    return (
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center">
            <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden border border-[#FFC000]">
              <div className="bg-white dark:bg-gray-800 border-b border-[#FFC000] px-8 py-6">
                <h1 className="text-3xl font-bold text-[#0E3056] dark:text-white text-center">Invalid Link</h1>
              </div>
              <div className="p-8 text-center">
                <ExclamationTriangleIcon className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  This password reset link is invalid or has expired.
                </p>
                <Link
                  to="/forgot-password"
                  className="inline-flex items-center justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-[#0E3056] bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Request New Reset Link
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  /**
   * Handle the sign-in redirect after successful password reset
   * Supports both internal navigation (AgentNexus) and external redirect (NexusChat)
   */
  const handleSignInRedirect = () => {
    if (postResetRedirectUrl) {
      // External redirect (e.g., to NexusChat login)
      // Check if it's a full URL or a relative path
      if (postResetRedirectUrl.startsWith('http://') || postResetRedirectUrl.startsWith('https://')) {
        window.location.href = postResetRedirectUrl;
      } else {
        // Relative path - use React Router navigation
        navigate(postResetRedirectUrl);
      }
    } else {
      // Default: navigate to AgentNexus login
      navigate('/login');
    }
  };

  // Success state - password reset complete
  if (success) {
    // Determine if we're redirecting to an external URL
    const isExternalRedirect = postResetRedirectUrl &&
      (postResetRedirectUrl.startsWith('http://') || postResetRedirectUrl.startsWith('https://'));

    return (
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center">
            <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden border border-[#FFC000]">
              <div className="bg-white dark:bg-gray-800 border-b border-[#FFC000] px-8 py-6">
                <h1 className="text-3xl font-bold text-[#0E3056] dark:text-white text-center">Password Reset</h1>
              </div>
              <div className="p-8 text-center">
                <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Your password has been successfully reset. You can now sign in with your new password.
                </p>
                {isExternalRedirect ? (
                  <button
                    onClick={handleSignInRedirect}
                    className="inline-flex items-center justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-[#0E3056] bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    Continue to Sign In
                  </button>
                ) : (
                  <Link
                    to={postResetRedirectUrl || '/login'}
                    className="inline-flex items-center justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-[#0E3056] bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    Sign In
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-center">
          <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden border border-[#FFC000]">
            {/* Header */}
            <div className="bg-white dark:bg-gray-800 border-b border-[#FFC000] px-8 py-6">
              <h1 className="text-3xl font-bold text-[#0E3056] dark:text-white text-center">Create New Password</h1>
            </div>

            {/* Form content */}
            <div className="p-8">
              {/* Error message */}
              {error && (
                <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                  <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                </div>
              )}

              {/* Reset form */}
              <form className="space-y-4" onSubmit={handleSubmit}>
                {/* New Password field */}
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    New Password
                  </label>
                  <div className="relative mt-1">
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="block w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      placeholder="Enter new password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      {showPassword ? (
                        <EyeSlashIcon className="h-5 w-5" />
                      ) : (
                        <EyeIcon className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Password requirements */}
                <div className="space-y-1 text-xs">
                  <p className={`flex items-center ${passwordStrength.hasMinLength ? 'text-green-600' : 'text-gray-500'}`}>
                    <span className="mr-2">{passwordStrength.hasMinLength ? '✓' : '○'}</span>
                    At least 8 characters
                  </p>
                  <p className={`flex items-center ${passwordStrength.hasUppercase ? 'text-green-600' : 'text-gray-500'}`}>
                    <span className="mr-2">{passwordStrength.hasUppercase ? '✓' : '○'}</span>
                    One uppercase letter
                  </p>
                  <p className={`flex items-center ${passwordStrength.hasLowercase ? 'text-green-600' : 'text-gray-500'}`}>
                    <span className="mr-2">{passwordStrength.hasLowercase ? '✓' : '○'}</span>
                    One lowercase letter
                  </p>
                  <p className={`flex items-center ${passwordStrength.hasNumber ? 'text-green-600' : 'text-gray-500'}`}>
                    <span className="mr-2">{passwordStrength.hasNumber ? '✓' : '○'}</span>
                    One number
                  </p>
                  <p className={`flex items-center ${passwordStrength.hasSpecial ? 'text-green-600' : 'text-gray-500'}`}>
                    <span className="mr-2">{passwordStrength.hasSpecial ? '✓' : '○'}</span>
                    One special character (!@#$%^&* etc.)
                  </p>
                </div>

                {/* Confirm Password field */}
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Confirm Password
                  </label>
                  <div className="relative mt-1">
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      required
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className={`block w-full px-3 py-2 pr-10 border rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${
                        confirmPassword.length > 0
                          ? passwordsMatch
                            ? 'border-green-500'
                            : 'border-red-500'
                          : 'border-gray-300 dark:border-gray-600'
                      }`}
                      placeholder="Confirm new password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      {showConfirmPassword ? (
                        <EyeSlashIcon className="h-5 w-5" />
                      ) : (
                        <EyeIcon className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                  {confirmPassword.length > 0 && !passwordsMatch && (
                    <p className="mt-1 text-xs text-red-500">Passwords do not match</p>
                  )}
                </div>

                {/* Submit button */}
                <div>
                  <button
                    type="submit"
                    disabled={loading || !isPasswordValid || !passwordsMatch}
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-[#0E3056] bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Resetting...' : 'Reset Password'}
                  </button>
                </div>
              </form>

              {/* Back to login link */}
              <div className="text-center mt-6">
                <Link
                  to="/login"
                  className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-500 dark:hover:text-primary-300"
                >
                  Back to Sign In
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ResetPasswordPage;
